/**
 * The member door (TRUS-778). Rendered at /member/home and /help.
 *
 * Signed out, it is the front door for someone who needs help: pick a need,
 * go to /member/join, and we find a place near them and stay with them until
 * they are connected. Nothing here is a dead end — the old page told people
 * without an invitation to "contact your care coordinator".
 *
 * Signed in, it is "my page": a greeting, the member's requests for help and
 * where each one stands, and links to messages, documents and their card.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, CreditCard, FileText, LogOut, MessageSquare, LayoutDashboard } from 'lucide-react';
import { getMemberData, getMemberToken, isAuthenticated, logoutMember } from '@/lib/memberAuth';
import {
  NEEDS,
  getMyRequests,
  parseInviteCode,
  type MemberRequest,
} from '@/lib/memberJoin';
import { IOS_APP_URL, ANDROID_APP_URL } from '@/lib/desktopDownloads';
import { MemberOrgSwitcher } from '@/components/member-portal/MemberOrgSwitcher';
import {
  CrisisLine,
  DOOR_INPUT,
  DOOR_PRIMARY,
  DOOR_QUIET,
  DOOR_SECONDARY,
  DoorLogo,
  DoorSection,
  DoorUtilityBar,
  NEED_ICONS,
  TalkToAPerson,
} from '@/components/member-portal/MemberDoorChrome';
import { AUTH_BACKDROP } from '@/src/platform/shared/components/authBackdrop';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';

interface MemberCard {
  id: number;
  card_status: string;
  remaining_balance: number;
}

export default function MemberHomePage() {
  return isAuthenticated() ? <SignedInHome /> : <SignedOutHome />;
}

// ---------------------------------------------------------------------------
// Signed out
// ---------------------------------------------------------------------------

function SignedOutHome() {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [invite, setInvite] = useState('');
  const [inviteError, setInviteError] = useState('');

  const submitInvite = (e: React.FormEvent) => {
    e.preventDefault();
    const token = parseInviteCode(invite);
    if (!token) {
      setInviteError(t('memberDoor.invite_not_recognised', 'We could not read that code. Check it and try again.'));
      return;
    }
    navigate(`/member/register/${encodeURIComponent(token)}`);
  };

  const steps = [
    {
      title: t('memberDoor.how_1_title', 'Tell us what you need'),
      body: t(
        'memberDoor.how_1_body',
        'Pick what you need help with and enter your ZIP code. We ask only what helps us find the right place.',
      ),
    },
    {
      title: t('memberDoor.how_2_title', 'See places near you'),
      body: t(
        'memberDoor.how_2_body',
        'Right away you see places that can help, with hours, what to bring and what to say.',
      ),
    },
    {
      title: t('memberDoor.how_3_title', 'We stay with you'),
      body: t(
        'memberDoor.how_3_body',
        'We ask a local organization to take your request, text you when they say yes, and check in until you get help.',
      ),
    },
  ];

  return (
    <div className={`${AUTH_BACKDROP} flex flex-col text-ink-700`}>
      <DoorUtilityBar showHelpLine />

      <header className="bg-surface border-b border-v2border">
        <DoorSection className="py-3 flex items-center justify-between gap-4">
          <Link to="/member/home" aria-label={t('memberDoor.home', 'WeaveIn home')}>
            <DoorLogo />
          </Link>
          <nav className="flex items-center gap-2">
            <a href="#how-it-works" className="hidden md:inline-flex items-center min-h-[44px] px-3 text-ink font-medium hover:text-v2accent-ink">
              {t('memberDoor.how_it_works', 'How it works')}
            </a>
            <Link to="/member/register" className="hidden md:inline-flex items-center min-h-[44px] px-3 text-ink font-medium hover:text-v2accent-ink">
              {t('memberDoor.i_have_an_invite', 'I have an invite')}
            </Link>
            <Link
              to="/member/login"
              className="inline-flex items-center min-h-[44px] px-4 rounded-lg border border-v2border text-ink font-semibold hover:border-v2accent"
            >
              {t('memberDoor.sign_in', 'Sign in')}
            </Link>
            <Link to="/member/join" className="hidden md:inline-flex items-center min-h-[44px] px-4 rounded-lg bg-v2accent text-white font-semibold hover:bg-v2accent/90">
              {t('memberDoor.get_help', 'Get help')}
            </Link>
          </nav>
        </DoorSection>
      </header>

      <main className="flex-1 bg-surface">
        {/* Hero + needs */}
        <DoorSection className="pt-8 pb-8 lg:pt-14 lg:pb-12 grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          <div className="flex flex-col gap-4">
            <h1 className="font-display text-[32px] lg:text-[44px] leading-[1.15] font-semibold tracking-[-0.02em] text-ink">
              {t('memberDoor.hero_title', 'Get help with food, housing, bills and more.')}
            </h1>
            <p className="text-lg text-ink-700">
              {t(
                'memberDoor.hero_body',
                'Tell us what you need. We find a place near you, and we check in until you are connected.',
              )}
            </p>
            <div className="hidden lg:flex gap-3 pt-2">
              <Link to="/member/join" className={DOOR_PRIMARY}>
                {t('memberDoor.get_help', 'Get help')}
              </Link>
              <Link to="/member/register" className={DOOR_SECONDARY}>
                {t('memberDoor.i_have_an_invite_code', 'I have an invite code')}
              </Link>
            </div>
            <p className="text-sm text-ink-700">
              {t('memberDoor.about_5_minutes', 'About 5 minutes')} ·{' '}
              {t('memberDoor.private', 'Private')} ·{' '}
              {t('memberDoor.no_ssn', 'No social security number needed')}
            </p>
          </div>

          <section aria-labelledby="needs-heading" className="flex flex-col gap-3">
            <h2 id="needs-heading" className="font-display text-xl font-semibold text-ink">
              {t('memberDoor.what_do_you_need', 'What do you need help with?')}
            </h2>
            <p className="hidden lg:block text-sm text-ink-700 -mt-1">
              {t('memberDoor.pick_one', 'Pick one to start. You can add more later.')}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              {NEEDS.filter((n) => n.code !== 'not_sure').map((need) => {
                const Icon = NEED_ICONS[need.code];
                return (
                  <Link
                    key={need.code}
                    to={`/member/join?need=${need.code}`}
                    className="flex flex-col gap-2 min-h-[84px] p-3.5 rounded-xl border border-v2border bg-surface text-ink font-semibold hover:border-v2accent hover:bg-v2accent-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-v2accent"
                  >
                    <Icon className="w-6 h-6 text-v2accent" aria-hidden="true" />
                    {t(need.labelKey, need.label)}
                  </Link>
                );
              })}
            </div>
            <Link to="/member/join?need=not_sure" className={DOOR_QUIET}>
              {t('memberDoor.not_sure_help_me', "I'm not sure, help me figure it out")}
            </Link>
            <div className="flex flex-col gap-3 pt-2 lg:hidden">
              <Link to="/member/join" className={DOOR_PRIMARY}>
                {t('memberDoor.get_help', 'Get help')}
              </Link>
              <Link to="/member/register" className={DOOR_SECONDARY}>
                {t('memberDoor.i_have_an_invite_code', 'I have an invite code')}
              </Link>
            </div>
          </section>
        </DoorSection>

        {/* How it works */}
        <section id="how-it-works" className="bg-paper border-y border-v2border">
          <DoorSection className="py-8 lg:py-12 flex flex-col gap-5">
            <h2 className="font-display text-[22px] font-semibold text-ink">
              {t('memberDoor.how_it_works', 'How it works')}
            </h2>
            <ol className="grid md:grid-cols-3 gap-5">
              {steps.map((step, i) => (
                <li key={step.title} className="flex gap-3.5">
                  <span className="flex-none w-8 h-8 rounded-full bg-v2accent text-white font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <div>
                    <p className="font-semibold text-ink">{step.title}</p>
                    <p className="text-ink-700">{step.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </DoorSection>
        </section>

        <DoorSection className="py-8 lg:py-12 grid lg:grid-cols-2 gap-10">
          {/* Invite code */}
          <section className="flex flex-col gap-3">
            <h2 className="font-display text-xl font-semibold text-ink">
              {t('memberDoor.already_working', 'Already working with a care team?')}
            </h2>
            <p className="text-ink-700">
              {t(
                'memberDoor.already_working_body',
                'If they sent you a text or email, open the link in it. Or type the code from it here.',
              )}
            </p>
            <form onSubmit={submitInvite} className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <div className="flex-1">
                <label htmlFor="home-invite" className="block text-sm font-medium text-ink mb-1">
                  {t('memberDoor.invite_code', 'Invite code')}
                </label>
                <input
                  id="home-invite"
                  value={invite}
                  onChange={(e) => {
                    setInvite(e.target.value);
                    setInviteError('');
                  }}
                  autoComplete="off"
                  className={DOOR_INPUT}
                />
              </div>
              <button type="submit" className={DOOR_SECONDARY}>
                {t('memberDoor.continue', 'Continue')}
              </button>
            </form>
            {inviteError && <p className="text-sm text-danger-ink">{inviteError}</p>}
          </section>

          <TalkToAPerson />
        </DoorSection>

        {/* App */}
        <section className="bg-paper border-t border-v2border">
          <DoorSection className="py-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="font-display text-xl font-semibold text-ink">
                {t('memberDoor.get_the_app', 'Get the WeaveIn app')}
              </h2>
              <p className="text-ink-700">
                {t(
                  'memberDoor.get_the_app_body',
                  'Messages from your navigator, your requests and your documents, on your phone.',
                )}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:w-80">
              <a href={IOS_APP_URL} target="_blank" rel="noopener noreferrer" className={DOOR_QUIET}>
                {t('memberDoor.app_store', 'App Store')}
              </a>
              <a href={ANDROID_APP_URL} target="_blank" rel="noopener noreferrer" className={DOOR_QUIET}>
                {t('memberDoor.google_play', 'Google Play')}
              </a>
            </div>
          </DoorSection>
        </section>
      </main>

      <DoorFooter />
    </div>
  );
}

function DoorFooter() {
  const { t } = useAppTranslation();
  const linkCls = 'inline-flex items-center min-h-[44px] text-v2accent-ink hover:underline';
  return (
    <footer className="bg-surface border-t border-v2border">
      <DoorSection className="py-6 flex flex-col gap-3">
        <CrisisLine />
        <nav className="flex flex-wrap gap-x-5 text-sm">
          <Link to="/consent/privacy-policy" className={linkCls}>
            {t('memberDoor.privacy', 'Privacy')}
          </Link>
          <Link to="/consent/terms-of-use" className={linkCls}>
            {t('memberDoor.terms', 'Terms')}
          </Link>
          <Link to="/" className={linkCls}>
            {t('memberDoor.for_organizations', 'For organizations')}
          </Link>
          <Link to="/login" className={linkCls}>
            {t('memberDoor.staff_sign_in', 'Staff sign in')}
          </Link>
        </nav>
        <p className="text-sm text-ink-500">
          © {new Date().getFullYear()} {t('memberDoor.copyright', 'WeaveIn.ai by Trust')}
        </p>
      </DoorSection>
    </footer>
  );
}

// ---------------------------------------------------------------------------
// Signed in
// ---------------------------------------------------------------------------

const PROGRESS: string[] = ['asked', 'matched', 'connected'];

function SignedInHome() {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const member = getMemberData();
  const [requests, setRequests] = useState<MemberRequest[] | null>(null);
  const [requestsFailed, setRequestsFailed] = useState(false);
  const [card, setCard] = useState<MemberCard | null>(null);

  useEffect(() => {
    let cancelled = false;
    getMyRequests()
      .then((rows) => {
        if (!cancelled) setRequests(rows);
      })
      .catch(() => {
        // Degrade quietly: the rest of the page still works without it.
        if (!cancelled) setRequestsFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const token = getMemberToken();
    if (!token || !member?.id) return;
    fetch(getWeaveinApiUrl(`/api/cards/member/${member.id}`), {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
        'Content-Type': 'application/json',
      },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data) return;
        const rows: any[] = Array.isArray(data) ? data : [data];
        const active = rows.find((c) => c?.card_status?.toLowerCase() === 'active');
        if (active) setCard({ ...active, remaining_balance: Number(active.remaining_balance) || 0 });
      })
      .catch(() => {});
    // member.id is read from storage once per mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    logoutMember();
    navigate('/member/home');
  };

  const needLabel = (code: string) => {
    const need = NEEDS.find((n) => n.code === code);
    if (need) return t(need.labelKey, need.label);
    return code.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
  };

  const statusLabel = (status: string) => {
    switch (status) {
      case 'asked':
        return t('memberDoor.status_asked', 'Waiting for a reply');
      case 'matched':
        return t('memberDoor.status_matched', 'Matched');
      case 'connected':
        return t('memberDoor.status_connected', 'Connected');
      case 'closed':
        return t('memberDoor.status_closed', 'Closed');
      default:
        return status;
    }
  };

  const progressLabel = (step: string) =>
    step === 'asked'
      ? t('memberDoor.progress_asked', 'Asked')
      : step === 'matched'
        ? t('memberDoor.progress_matched', 'Matched')
        : t('memberDoor.progress_connected', 'Connected');

  const tileCls =
    'flex items-center gap-3 min-h-[56px] px-4 rounded-xl border border-v2border bg-surface text-ink font-semibold hover:border-v2accent hover:bg-v2accent-soft';

  return (
    <div className={`${AUTH_BACKDROP} flex flex-col text-ink-700`}>
      <DoorUtilityBar />
      <header className="bg-surface border-b border-v2border">
        <DoorSection className="py-3 flex items-center justify-between gap-3">
          <Link to="/member/home" aria-label={t('memberDoor.home', 'WeaveIn home')}>
            <DoorLogo />
          </Link>
          <div className="flex items-center gap-2">
            <MemberOrgSwitcher />
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 min-h-[44px] px-3 rounded-lg text-ink font-medium hover:bg-paper-2"
            >
              <LogOut className="w-4 h-4" aria-hidden="true" />
              {t('memberDoor.sign_out', 'Sign out')}
            </button>
          </div>
        </DoorSection>
      </header>

      <main className="flex-1">
        <DoorSection narrow className="py-6 lg:py-10 flex flex-col gap-6">
          <h1 className="font-display text-[28px] leading-tight font-semibold tracking-[-0.02em] text-ink">
            {member?.first_name
              ? t('memberDoor.hi_name', 'Hi {{name}}', { name: member.first_name })
              : t('memberDoor.hi', 'Hi')}
          </h1>

          {!requestsFailed && (
            <section aria-labelledby="requests-heading" className="flex flex-col gap-3">
              <h2 id="requests-heading" className="font-display text-lg font-semibold text-ink">
                {t('memberDoor.your_requests', 'Your requests')}
              </h2>
              {requests === null ? (
                <div className="h-20 rounded-xl bg-paper-2 animate-pulse" aria-hidden="true" />
              ) : requests.length === 0 ? (
                <p className="text-ink-700">
                  {t('memberDoor.no_requests', 'You have not asked for help here yet.')}
                </p>
              ) : (
                <ul className="flex flex-col gap-3">
                  {requests.map((req) => {
                    const reached = PROGRESS.indexOf(req.status);
                    return (
                      <li key={req.id} className="rounded-xl border border-v2border bg-surface p-4 flex flex-col gap-2">
                        <div className="flex items-center justify-between gap-3">
                          <p className="font-semibold text-ink">{needLabel(req.need)}</p>
                          <span
                            className={`text-sm font-semibold px-2.5 py-0.5 rounded-full ${
                              req.status === 'connected'
                                ? 'bg-v2accent-soft text-v2accent-ink'
                                : 'bg-paper-2 text-ink-700'
                            }`}
                          >
                            {statusLabel(req.status)}
                          </span>
                        </div>
                        {req.status !== 'closed' && (
                          <ol className="flex items-center gap-2 text-sm" aria-label={t('memberDoor.progress', 'Progress')}>
                            {PROGRESS.map((step, i) => (
                              <li key={step} className="flex items-center gap-2">
                                <span
                                  className={`w-2.5 h-2.5 rounded-full ${i <= reached ? 'bg-v2accent' : 'bg-v2border'}`}
                                  aria-hidden="true"
                                />
                                <span className={i <= reached ? 'text-ink font-medium' : 'text-ink-500'}>
                                  {progressLabel(step)}
                                </span>
                                {i < PROGRESS.length - 1 && <span className="text-ink-400" aria-hidden="true">·</span>}
                              </li>
                            ))}
                          </ol>
                        )}
                        {req.org_name && <p className="text-sm text-ink-700">{req.org_name}</p>}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          )}

          <Link to="/member/join" className={DOOR_SECONDARY}>
            {t('memberDoor.ask_something_else', 'Ask for help with something else')}
          </Link>

          <nav className="flex flex-col gap-2.5" aria-label={t('memberDoor.my_things', 'My things')}>
            <Link to="/member/inbox" className={tileCls}>
              <MessageSquare className="w-5 h-5 text-v2accent" aria-hidden="true" />
              <span className="flex-1">{t('memberDoor.messages', 'Messages')}</span>
              <ChevronRight className="w-5 h-5 text-ink-400" aria-hidden="true" />
            </Link>
            <Link to="/member/documents" className={tileCls}>
              <FileText className="w-5 h-5 text-v2accent" aria-hidden="true" />
              <span className="flex-1">{t('memberDoor.my_documents', 'My documents')}</span>
              <ChevronRight className="w-5 h-5 text-ink-400" aria-hidden="true" />
            </Link>
            <Link to="/member/incentives?tab=card" className={tileCls}>
              <CreditCard className="w-5 h-5 text-v2accent" aria-hidden="true" />
              <span className="flex-1">{t('memberDoor.my_card', 'My card')}</span>
              {card && (
                <span className="text-sm font-medium text-ink-700">
                  {t('memberDoor.card_left', '{{amount}} left', { amount: `$${card.remaining_balance.toFixed(2)}` })}
                </span>
              )}
              <ChevronRight className="w-5 h-5 text-ink-400" aria-hidden="true" />
            </Link>
            <Link to="/member/dashboard" className={tileCls}>
              <LayoutDashboard className="w-5 h-5 text-v2accent" aria-hidden="true" />
              <span className="flex-1">{t('memberDoor.my_dashboard', 'My dashboard')}</span>
              <ChevronRight className="w-5 h-5 text-ink-400" aria-hidden="true" />
            </Link>
          </nav>

          <TalkToAPerson />
          <CrisisLine />
        </DoorSection>
      </main>
    </div>
  );
}
